import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { isPanelGlobalThemePath } from '../../constants/panelThemeScope';
import brasao from '../../assets/img/brasao.png';

// Importa os estilos específicos do painel
import '../../assets/css/painel.css';
import '../../assets/css/theme-ux-overrides.css';
import '../../assets/css/panel-theme.css';

const DASHBOARD_THEME_STORAGE_KEY = 'dashboardLightMode';
const DASHBOARD_THEME_EVENT = 'dashboard-theme-changed';
const PANEL_THEME_STORAGE_KEY = 'panelLightMode';
const PANEL_THEME_EVENT = 'panel-theme-changed';
const THEME_LIGHT = 'light';
const THEME_DARK = 'dark';

const normalizeThemePreference = (value) => (value === THEME_DARK ? THEME_DARK : THEME_LIGHT);

const isLightModeFromThemePreference = (value) => normalizeThemePreference(value) === THEME_LIGHT;

const getInitialThemeMode = (storageKey, fallbackTheme) => {
  if (typeof window === 'undefined') return isLightModeFromThemePreference(fallbackTheme);

  const saved = sessionStorage.getItem(storageKey);
  if (saved !== null) return saved === 'true';

  return isLightModeFromThemePreference(fallbackTheme);
};

function PainelLayout() {
  const [sidebarOculta, setSidebarOculta] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 768px)').matches;
  });
  const [tabelasExpanded, setTabelasExpanded] = useState(false);
  const [manualExpanded, setManualExpanded] = useState(false);
  const { user, logout, canAccess, hasAnyPermission } = useAuth(); // Acessa o contexto de autenticação
  const location = useLocation();
  const navigate = useNavigate(); // Hook para navegação
  const isDashboardPath = location.pathname === '/painel/dashboard';
  const isPanelGlobalThemeRoute = isPanelGlobalThemePath(location.pathname);
  const [dashboardLightMode, setDashboardLightMode] = useState(() => {
    return getInitialThemeMode(
      DASHBOARD_THEME_STORAGE_KEY,
      user?.theme_preferences?.dashboard_theme
    );
  });
  const [panelLightMode, setPanelLightMode] = useState(() => {
    return getInitialThemeMode(
      PANEL_THEME_STORAGE_KEY,
      user?.theme_preferences?.panel_theme
    );
  });
  const isDashboardLightShell = isDashboardPath && dashboardLightMode;
  const isPanelLightShell = isPanelGlobalThemeRoute && panelLightMode;

  const toggleSidebar = () => {
    setSidebarOculta(!sidebarOculta);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const emitResize = () => {
      window.dispatchEvent(new Event('resize'));
    };

    emitResize();

    // Reemite após a transição da sidebar para componentes que medem largura no fim da animação.
    const timeoutId = window.setTimeout(emitResize, 320);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [sidebarOculta]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(max-width: 768px)');
    const handleChange = (e) => {
      setSidebarOculta(e.matches ? true : false);
    };
    if (mql.addEventListener) {
      mql.addEventListener('change', handleChange);
    } else {
      mql.addListener(handleChange);
    }
    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener('change', handleChange);
      } else {
        mql.removeListener(handleChange);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(max-width: 768px)').matches) return;
    setSidebarOculta(true);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(max-width: 768px)').matches) return;
    if (!isDashboardPath) return;
    setSidebarOculta(true);
  }, [isDashboardPath]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!user?.theme_preferences) return;

    const dashboardTheme = user.theme_preferences.dashboard_theme;
    const panelTheme = user.theme_preferences.panel_theme;

    if (dashboardTheme === THEME_LIGHT || dashboardTheme === THEME_DARK) {
      const nextDashboardMode = isLightModeFromThemePreference(dashboardTheme);
      setDashboardLightMode(nextDashboardMode);
      sessionStorage.setItem(DASHBOARD_THEME_STORAGE_KEY, String(nextDashboardMode));
      window.dispatchEvent(new CustomEvent(DASHBOARD_THEME_EVENT, { detail: { lightMode: nextDashboardMode } }));
    }

    if (panelTheme === THEME_LIGHT || panelTheme === THEME_DARK) {
      const nextPanelMode = isLightModeFromThemePreference(panelTheme);
      setPanelLightMode(nextPanelMode);
      sessionStorage.setItem(PANEL_THEME_STORAGE_KEY, String(nextPanelMode));
      window.dispatchEvent(new CustomEvent(PANEL_THEME_EVENT, { detail: { lightMode: nextPanelMode } }));
    }
  }, [user]);

  const persistThemePreferences = async (payload) => {
    try {
      await api.put('/minha-conta/theme', payload);
    } catch (_) {
      // Keep local fallback through sessionStorage.
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isDashboardPath) return;

    const saved = sessionStorage.getItem(DASHBOARD_THEME_STORAGE_KEY);
    if (saved === null) {
      sessionStorage.setItem(DASHBOARD_THEME_STORAGE_KEY, 'true');
      setDashboardLightMode(true);
      return;
    }

    setDashboardLightMode(saved === 'true');
  }, [isDashboardPath]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isPanelGlobalThemeRoute) return;

    const saved = sessionStorage.getItem(PANEL_THEME_STORAGE_KEY);
    if (saved === null) {
      sessionStorage.setItem(PANEL_THEME_STORAGE_KEY, 'true');
      setPanelLightMode(true);
      return;
    }

    setPanelLightMode(saved === 'true');
  }, [isPanelGlobalThemeRoute]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const syncDashboardTheme = (event) => {
      const nextMode = event?.detail?.lightMode;
      if (typeof nextMode === 'boolean') {
        setDashboardLightMode(nextMode);
        return;
      }

      const saved = sessionStorage.getItem(DASHBOARD_THEME_STORAGE_KEY);
      setDashboardLightMode(saved !== 'false');
    };

    window.addEventListener(DASHBOARD_THEME_EVENT, syncDashboardTheme);
    return () => {
      window.removeEventListener(DASHBOARD_THEME_EVENT, syncDashboardTheme);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const syncPanelTheme = (event) => {
      const nextMode = event?.detail?.lightMode;
      if (typeof nextMode === 'boolean') {
        setPanelLightMode(nextMode);
        return;
      }

      const saved = sessionStorage.getItem(PANEL_THEME_STORAGE_KEY);
      setPanelLightMode(saved !== 'false');
    };

    window.addEventListener(PANEL_THEME_EVENT, syncPanelTheme);
    return () => {
      window.removeEventListener(PANEL_THEME_EVENT, syncPanelTheme);
    };
  }, []);

  useEffect(() => {
    const mainContent = document.querySelector('.main-content');
    const painelContainer = document.querySelector('.painel-container');
    const mainLightClassName = 'panel-main-content-light';
    const shellLightClassName = 'panel-shell-theme-light';

    if (!mainContent && !painelContainer) return undefined;

    if (isPanelLightShell) {
      if (mainContent) mainContent.classList.add(mainLightClassName);
      if (painelContainer) painelContainer.classList.add(shellLightClassName);
    } else {
      if (mainContent) mainContent.classList.remove(mainLightClassName);
      if (painelContainer) painelContainer.classList.remove(shellLightClassName);
    }

    return () => {
      if (mainContent) mainContent.classList.remove(mainLightClassName);
      if (painelContainer) painelContainer.classList.remove(shellLightClassName);
    };
  }, [isPanelLightShell]);

  // Função para lidar com o logout
  const handleLogout = async () => {
    await logout();
    // Redireciona explicitamente para a página inicial
    navigate('/', { replace: true });
  };

  const getTodayYmd = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const handleDashboardMenuClick = (e) => {
    const isDashboardCurrentPath = location.pathname === '/painel/dashboard';
    if (!isDashboardCurrentPath) return;

    e.preventDefault();
    navigate('/painel/dashboard', {
      state: {
        dashboardContext: {
          status: 'open',
          date: getTodayYmd(),
          q: '',
        },
        dashboardResetAt: Date.now(),
      },
    });
  };

  const handleDashboardThemeToggle = () => {
    if (typeof window === 'undefined') return;

    const nextMode = !dashboardLightMode;
    setDashboardLightMode(nextMode);
    sessionStorage.setItem(DASHBOARD_THEME_STORAGE_KEY, String(nextMode));
    window.dispatchEvent(new CustomEvent(DASHBOARD_THEME_EVENT, { detail: { lightMode: nextMode } }));
    persistThemePreferences({
      dashboard_theme: nextMode ? THEME_LIGHT : THEME_DARK,
    });
  };

  const handlePanelThemeToggle = () => {
    if (typeof window === 'undefined') return;

    const nextMode = !panelLightMode;
    setPanelLightMode(nextMode);
    sessionStorage.setItem(PANEL_THEME_STORAGE_KEY, String(nextMode));
    window.dispatchEvent(new CustomEvent(PANEL_THEME_EVENT, { detail: { lightMode: nextMode } }));
    persistThemePreferences({
      panel_theme: nextMode ? THEME_LIGHT : THEME_DARK,
    });
  };

  const activeThemeScope = isDashboardPath ? 'dashboard' : (isPanelGlobalThemeRoute ? 'panel-global' : 'none');
  const showThemeToggle = activeThemeScope !== 'none';
  const activeThemeLightMode = activeThemeScope === 'dashboard' ? dashboardLightMode : panelLightMode;
  const activeThemeLabel = activeThemeScope === 'dashboard' ? 'dashboard' : 'painel';
  const handleActiveThemeToggle = activeThemeScope === 'dashboard'
    ? handleDashboardThemeToggle
    : handlePanelThemeToggle;
  const panelTheme = panelLightMode ? 'light' : 'dark';
  const mainContentClassName = isPanelGlobalThemeRoute
    ? `main-content panel-theme panel-theme--${panelTheme}`
    : 'main-content';

  return (
    <div className="painel-container">
      {/* Barra Superior */}
      <div className="topbar">
        <div className="menu-toggle" onClick={toggleSidebar}>
          <i className="bi bi-list"></i>
        </div>
        <div className="topbar-brand">
          <img src={brasao} alt="SEJUS" className="topbar-logo" />
          <span>NAI Movimentação</span>
        </div>

        {/* Wrapper para os menus à direita */}
        <div className="topbar-nav ms-auto d-flex align-items-center">
          {showThemeToggle && (
            <button
              type="button"
              className={`topbar-theme-icon-btn ${activeThemeLightMode ? 'is-light' : 'is-dark'}`}
              onClick={handleActiveThemeToggle}
              aria-label={`Alternar tema do ${activeThemeLabel}`}
              aria-pressed={activeThemeLightMode}
              title={activeThemeLightMode ? 'Tema claro ativo' : 'Tema escuro ativo'}
            >
              <i className={`bi ${activeThemeLightMode ? 'bi-sun-fill' : 'bi-moon-stars-fill'}`}></i>
            </button>
          )}

          {/* Menu do Usuário */}
          <div className="dropdown">
            <button
              className="btn btn-glass dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              style={{ color: '#84a98c' }}
            >
              <i className="bi bi-person-circle me-2"></i>
              {user ? user.name : 'Usuário'}
            </button>
            <ul
              className={`dropdown-menu dropdown-menu-end ${(isDashboardLightShell || isPanelLightShell) ? 'panel-shell-user-menu-light' : 'dropdown-menu-dark'}`}
            >
              <li>
                <NavLink to="/painel/minha-conta" className="dropdown-item">
                  <i className="bi bi-person-circle me-2"></i>Minha Conta
                </NavLink>
              </li>
              <li>
                <a className="dropdown-item" href="#" onClick={handleLogout}>
                  <i className="bi bi-box-arrow-right me-2"></i>Sair
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Menu Lateral */}
      <div className={`sidebar ${sidebarOculta ? 'sidebar-hidden' : 'sidebar-visible'}`}>
        <ul className="sidebar-menu">
          <li className="sidebar-item">
            <NavLink
              to="/painel/dashboard"
              className="sidebar-link"
              onClick={handleDashboardMenuClick}
            >
              <i className="bi bi-speedometer2"></i>
              <span>Painel</span>
            </NavLink>
          </li>
          
          {/* Menu de Adolescentes - Baseado em permissões */}
          {canAccess('adolescentes', 'view') && (
            <li className="sidebar-item">
              <NavLink to="/painel/adolescentes" className="sidebar-link">
                <i className="bi bi-people"></i>
                <span>Adolescentes</span>
              </NavLink>
            </li>
          )}
          
          {/* Menu Tabelas de Apoio com Submenu */}
          {hasAnyPermission([
            'config.especialistas',
            'config.situacoes',
            'config.unidades',
            'config.meios_entrada',
            'config.meios_saida',
            'config.modulos',
            'config.decisoes',
            'config.motivos_mba',
            'config.orgaos',
            'config.perfis',
            'config.promotores',
            'usuarios.view'
          ]) && (
            <li className="sidebar-item">
              <div
                className="sidebar-link"
                onClick={() => setTabelasExpanded(!tabelasExpanded)}
                style={{ cursor: 'pointer' }}
              >
                <i className="bi bi-table"></i>
                <span>Tabelas de Apoio</span>
                <i className={`bi bi-chevron-${tabelasExpanded ? 'down' : 'right'} ms-auto`}></i>
              </div>
              {tabelasExpanded && (
                <ul className="sidebar-submenu">
                  {canAccess('config', 'especialistas') && (
                    <li><NavLink to="/painel/especialistas" className="sidebar-sublink"><i className="bi bi-bookmarks me-2"></i>Especialistas</NavLink></li>
                  )}
                  {canAccess('config', 'unidades') && (
                    <li><NavLink to="/painel/unidades" className="sidebar-sublink"><i className="bi bi-building me-2"></i>Unidades</NavLink></li>
                  )}
                  {canAccess('config', 'modulos') && (
                    <li><NavLink to="/painel/modulos" className="sidebar-sublink"><i className="bi bi-view-stacked me-2"></i>Módulos</NavLink></li>
                  )}
                  {canAccess('config', 'meios_entrada') && (
                    <li><NavLink to="/painel/meios-entrada" className="sidebar-sublink"><i className="bi bi-box-arrow-in-right me-2"></i>Meios de Entrada</NavLink></li>
                  )}
                  {canAccess('config', 'meios_saida') && (
                    <li><NavLink to="/painel/meios-saida" className="sidebar-sublink"><i className="bi bi-box-arrow-right me-2"></i>Meios de Saída</NavLink></li>
                  )}
                  {canAccess('config', 'decisoes') && (
                    <li><NavLink to="/painel/decisoes" className="sidebar-sublink"><i className="bi bi-pen me-2"></i>Decisões</NavLink></li>
                  )}
                  {canAccess('config', 'motivos_mba') && (
                    <li><NavLink to="/painel/motivos-mba" className="sidebar-sublink"><i className="bi bi-clipboard-data me-2"></i>Motivos MBA</NavLink></li>
                  )}
                  {canAccess('config', 'orgaos') && (
                    <li><NavLink to="/painel/orgaos" className="sidebar-sublink"><i className="bi bi-bank me-2"></i>Órgãos</NavLink></li>
                  )}
                  {canAccess('config', 'perfis') && (
                    <li><NavLink to="/painel/perfis" className="sidebar-sublink"><i className="bi bi-person-badge me-2"></i>Perfis</NavLink></li>
                  )}
                  {canAccess('config', 'promotores') && (
                    <li><NavLink to="/painel/promotores" className="sidebar-sublink"><i className="bi bi-person-vcard me-2"></i>Promotores</NavLink></li>
                  )}
                  {canAccess('usuarios', 'view') && (
                    <li><NavLink to="/painel/usuarios" className="sidebar-sublink"><i className="bi bi-people-fill me-2"></i>Usuários</NavLink></li>
                  )}
                </ul>
              )}
            </li>
          )}

          {/* Menu de Relatórios - Baseado em permissões */}
          {canAccess('relatorios', 'view') && (
            <li className="sidebar-item">
              <NavLink to="/painel/relatorios" className="sidebar-link">
                <i className="bi bi-pie-chart"></i>
                <span>Relatórios</span>
              </NavLink>
            </li>
          )}
          {/* Link Dashboard de Logs - Apenas para admins */}
          {canAccess('admin', 'logs') && (
            <li className="sidebar-item">
              <NavLink to="/painel/logs" className="sidebar-link">
                <i className="bi bi-activity"></i>
                <span>Logs do Sistema</span>
              </NavLink>
            </li>
          )}
          {/* Menu Manual do Usuário com Submenu */}
          <li className="sidebar-item">
            <div 
              className="sidebar-link" 
              onClick={() => setManualExpanded(!manualExpanded)}
              style={{ cursor: 'pointer' }}
            >
              <i className="bi bi-book"></i>
              <span>Manual do Usuário</span>
              <i className={`bi bi-chevron-${manualExpanded ? 'down' : 'right'} ms-auto`}></i>
            </div>
            {manualExpanded && (
              <ul className="sidebar-submenu">
                <li>
                  <NavLink to="/painel/manual" end className="sidebar-sublink">
                    <i className="bi bi-house-door me-2"></i>
                    Início
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/painel/manual/introducao" className="sidebar-sublink">
                    <i className="bi bi-info-circle me-2"></i>
                    Introdução ao Sistema
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/painel/manual/primeiro-acesso" className="sidebar-sublink">
                    <i className="bi bi-key me-2"></i>
                    Primeiro Acesso
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/painel/manual/adolescentes" className="sidebar-sublink">
                    <i className="bi bi-people me-2"></i>
                    Adolescentes
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/painel/manual/movimentacoes" className="sidebar-sublink">
                    <i className="bi bi-arrow-left-right me-2"></i>
                    Movimentações
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/painel/manual/atendimentos" className="sidebar-sublink">
                    <i className="bi bi-clipboard-check me-2"></i>
                    Atendimentos
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/painel/manual/dashboard" className="sidebar-sublink">
                    <i className="bi bi-speedometer2 me-2"></i>
                    Painel de Controle
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/painel/manual/relatorios" className="sidebar-sublink">
                    <i className="bi bi-pie-chart me-2"></i>
                    Relatórios
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/painel/manual/usuarios" className="sidebar-sublink">
                    <i className="bi bi-person-badge me-2"></i>
                    Gerenciar Usuários
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/painel/manual/configuracoes" className="sidebar-sublink">
                    <i className="bi bi-gear me-2"></i>
                    Configurações do Sistema
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/painel/manual/minha-conta" className="sidebar-sublink">
                    <i className="bi bi-person-circle me-2"></i>
                    Minha Conta
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/painel/manual/logs" className="sidebar-sublink">
                    <i className="bi bi-activity me-2"></i>
                    Logs do Sistema
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/painel/manual/perguntas-frequentes" className="sidebar-sublink">
                    <i className="bi bi-question-circle me-2"></i>
                    Perguntas Frequentes
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/painel/manual/glossario" className="sidebar-sublink">
                    <i className="bi bi-bookmark me-2"></i>
                    Glossário de Termos
                  </NavLink>
                </li>
              </ul>
            )}
          </li>                    
        </ul>
      </div>

      {/* Conteúdo Principal */}
      <div className={mainContentClassName}>
        <Outlet />
      </div>
    </div>
  );
}

export default PainelLayout;
